import { defineComponent as _defineComponent } from 'vue'
import AutoCompleteChips from '@/components/vision/next/inputs/AutoCompleteChips.vue'
import InputWrapper from '@/components/vision/Inputs/InputWrapper.vue'
import { computed, nextTick, ref, useSlots } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useFloating, autoUpdate, flip, offset, size } from '@floating-ui/vue'
import { useAutoCompleteContext } from './useAutoComplete'


export default /*#__PURE__*/_defineComponent({
  __name: 'AutoCompleteShell',
  props: {
    name: null,
    label: null,
    placeholder: null,
    hint: null,
    disabled: { type: Boolean },
    errorMessages: null,
    smallChips: { type: Boolean },
    keepValueOnClickingOutside: { type: Boolean },
    variableHeight: { type: Boolean }
  },
  emits: ["update:search", "clear", "open", "close", "close-expand"],
  setup(__props: any, { expose, emit }: { emit: ({
  (event: 'update:search', value: string): void
  (event: 'clear'): void
  (event: 'open'): void
  (event: 'close'): void
  (event: 'close-expand', enableEmitCustomSearchValue: boolean): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  name?: string
  label: string
  placeholder?: string
  hint?: string
  disabled?: boolean
  errorMessages?: string[]
  smallChips?: boolean
  keepValueOnClickingOutside?: boolean
  variableHeight?: boolean
}





const slots = useSlots()

const inputRef = ref<HTMLInputElement | null>(null)
const popoverRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const autocompleteRef = ref<HTMLElement | null>(null)

const {
  chips,
  inputText,
  handleBackspace,
  removeValue,
  expanded,
  setExpanded,
  setModified,
} = useAutoCompleteContext()

const { floatingStyles } = useFloating(containerRef, popoverRef, {
  strategy: 'fixed',
  open: expanded,
  middleware: [
    flip(),
    offset(4),
    size({
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        })
      },
    }),
  ],
  whileElementsMounted(...args) {
    return autoUpdate(...args, { animationFrame: true })
  },
})

onClickOutside(autocompleteRef, () => {
  if (!props.keepValueOnClickingOutside) {
    setExpanded(false)
    emit('close-expand', true)
  }
})

const showMiniLabel = computed(
  () => Boolean(inputText.value || chips.value.length) && props.label !== ''
)

const placeholderText = computed(() => {
  // if there is an explict placeholder set, show that
  if (props.placeholder && !inputText.value) {
    return props.placeholder
  }
  // otherwise show the label, but only if there is no value
  if (!inputText.value && !chips.value.length) {
    return props.label
  }
})

const hasPopover = computed(() => {
  return slots.popover
})

const showClearButton = computed(() => {
  return inputText.value || chips.value.length
})

const handleClearValue = () => {
  emit('clear')
  close()
}

const handleToggle = (e: Event) => {
  e.stopImmediatePropagation()
  e.stopPropagation()
  if (expanded.value) {
    close()
  } else {
    inputRef.value?.focus()
  }
}

const handleClickWrapper = () => {
  if (!expanded.value && !props.disabled) {
    inputRef.value?.focus()
  }
}

const updateSearch = (e: Event) => {
  setModified(true)
  emit('update:search', (e.target as HTMLInputElement).value)
}

const handleFocus = () => {
  if (!expanded.value) {
    setExpanded(true)
    setModified(false)

    nextTick().then(() => {
      emit('open')
    })
  }
}

const handleFocusOut = (event: FocusEvent) => {
  // event.relatedTarget is the component that is gaining focus.
  // In Safari, onClick event on button won't focus the button, so event.relatedTarget = null
  // In other browsers, onClick event on button will focus it, so event.relatedTarget = button itself
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
  if (
    event.relatedTarget &&
    !autocompleteRef.value?.contains(event.relatedTarget as Node)
  ) {
    if (hasPopover?.value) {
      close(true)
    }
  }
}

const focus = () => {
  const checkAndFocus = () => {
    const input = inputRef.value
    if (input && input.offsetParent !== null) {
      input.focus()
    } else {
      requestAnimationFrame(checkAndFocus)
    }
  }
  nextTick(() => checkAndFocus())
}

const close = (enableEmitCustomSearchValue = false) => {
  setExpanded(false)
  setModified(false)
  emit('close')
  emit('close-expand', enableEmitCustomSearchValue)
}

expose({
  input: inputRef,
  setModified,
  close,
  focus,
})

const inputBindClass = computed(() => {
  if (chips.value.length === 0) {
    return { 'input-expanded': true }
  }
  return {
    'input-expanded': expanded.value,
    'input-collapsed': !expanded.value,
  }
})

const popOverHeight = computed(() => {
  if (props.variableHeight) {
    return { 'auto-height': true, popover: true }
  }
  return { 'full-height': true, popover: true }
})

return { __sfc: true,props, emit, slots, inputRef, popoverRef, containerRef, autocompleteRef, chips, inputText, handleBackspace, removeValue, expanded, setExpanded, setModified, floatingStyles, showMiniLabel, placeholderText, hasPopover, showClearButton, handleClearValue, handleToggle, handleClickWrapper, updateSearch, handleFocus, handleFocusOut, focus, close, inputBindClass, popOverHeight, AutoCompleteChips, InputWrapper }
}

})